import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  ActionButton,
  Flex,
  Item,
  ProgressCircle,
  TooltipTrigger,
  Tooltip
} from '@adobe/react-spectrum';
import { Menu, MenuTrigger, Text } from '@adobe/react-spectrum';
import { Dialog, DialogTrigger, Content } from '@adobe/react-spectrum';
import { CreateMoment } from './CreateMoment';
import Add from '@spectrum-icons/workflow/Add';
import LockClosed from '@spectrum-icons/workflow/LockClosed';
import styles from './CreateButton.module.scss';
// Removed separate Redux Provider - using main app store instead
import { useRsdForms } from './store';
import { useEventKeyDatePermissions } from '../../utils/rbacHandler';
import { useSelector } from 'react-redux';
import {
  selectUserProfile,
  selectProfileLoading
} from '../../pages/Layout/slice/selectors';
import { useSlideoutUrl } from '../../hooks/useSlideoutUrl';
import { RSD_CREATE_IAM_GROUP } from 'utils/constants';
import { isLegacyOfferOrPromoMomentType } from './utils/legacyOfferPromoMomentType';

interface CreateButtonProps {
  onSuccess?: (message: string) => void;
}

const CreateButtonContent = ({ onSuccess }: CreateButtonProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { isAdmin, canCreateRSD, canCreateEvents, canCreateKeyDates } =
    useEventKeyDatePermissions();
  const { isSlideoutOpen } = useSlideoutUrl();

  // Get user profile loading state to prevent premature rendering
  const userProfile = useSelector(selectUserProfile);
  const isUserProfileLoading = useSelector(selectProfileLoading);

  // Now using main app store - user profile should be accessible

  const {
    momentTypes,
    loadingMomentTypes,
    momentTypesError,
    loadMoments,
    selectMomentType,
    selectedMomentType,
    loadMomentTypes,
    loadAllFormConfigs,
    allFormConfigs,
    loadingAllFormConfigs
  } = useRsdForms();

  useEffect(() => {
    // Load moments and types when component mounts
    loadMoments();
    loadMomentTypes();
  }, [loadMoments, loadMomentTypes]);

  // Load form configs when menu is opened
  useEffect(() => {
    if (isMenuOpen && !allFormConfigs && !loadingAllFormConfigs) {
      loadAllFormConfigs();
    }
  }, [isMenuOpen, allFormConfigs, loadingAllFormConfigs, loadAllFormConfigs]);

  // Close menu and dialog when slideout panel opens
  useEffect(() => {
    if (isSlideoutOpen) {
      setIsMenuOpen(false);
      setIsDialogOpen(false);
    }
  }, [isSlideoutOpen]);

  const handleItemSelect = useCallback(
    (key: React.Key) => {
      if (!canCreateRSD) return;

      const momentType = momentTypes.find(type => type.id === key);
      if (momentType) {
        if (isLegacyOfferOrPromoMomentType(momentType)) {
          return;
        }
        const isEventOrKeyDate =
          momentType.name === 'Event' || momentType.name === 'Key Date';

        if (isUserProfileLoading) {
          console.warn('User profile still loading, please wait...');
          return;
        }

        if (isEventOrKeyDate && !isAdmin) {
          console.warn(`${momentType.name} creation requires admin privileges`);
          return;
        }

        selectMomentType(momentType.id);
        setIsDialogOpen(true);
      }
    },
    [canCreateRSD, momentTypes, isUserProfileLoading, isAdmin, selectMomentType]
  );

  const disabledKeys = useMemo(
    () =>
      momentTypes
        .filter(type => {
          if (type.name === 'Event') return !canCreateEvents;
          if (type.name === 'Key Date') return !canCreateKeyDates;
          return false;
        })
        .map(type => type.id),
    [momentTypes, canCreateEvents, canCreateKeyDates]
  );

  return (
    <Flex
      direction="column"
      UNSAFE_className={styles.buttonContainer}
      data-testid="create-button-container"
    >
      {!canCreateRSD ? (
        <TooltipTrigger delay={0}>
          <ActionButton
            aria-label={'Create - no access'}
            UNSAFE_className={`button ${styles.disabledButton}`}
            data-testid="create-button-action"
            onPress={() => {}}
          >
            <LockClosed size="S" data-testid="create-button-lock-icon" />
            <Text
              UNSAFE_className={styles.buttonText}
              data-testid="create-button-text"
            >
              Create
            </Text>
          </ActionButton>
          <Tooltip>
            <span>
              Access restricted. Please join{' '}
              <strong>{RSD_CREATE_IAM_GROUP}</strong> IAM group to enable RSD
              creation.
            </span>
          </Tooltip>
        </TooltipTrigger>
      ) : (
        <MenuTrigger
          isOpen={isMenuOpen}
          closeOnSelect={true}
          onOpenChange={setIsMenuOpen}
          data-testid="create-button-menu-trigger"
        >
          <ActionButton
            aria-label={'Create'}
            UNSAFE_className="button"
            data-testid="create-button-action"
          >
            <Add data-testid="create-button-add-icon" />
            <Text
              UNSAFE_className={styles.buttonText}
              data-testid="create-button-text"
            >
              Create
            </Text>
          </ActionButton>
          <Menu
            UNSAFE_className={styles.menu}
            onAction={handleItemSelect}
            disabledKeys={disabledKeys}
            data-testid="create-button-menu"
          >
            {loadingMomentTypes ? (
              <Item key="" data-testid="create-button-loading-item">
                <Text data-testid="create-button-loading-text">
                  Loading moment types...
                </Text>
              </Item>
            ) : momentTypesError ? (
              <Item key="" data-testid="create-button-error-item">
                <Text data-testid="create-button-error-text">
                  Failed to load moment types
                </Text>
              </Item>
            ) : momentTypes.length === 0 ? (
              <Item key="" data-testid="create-button-no-types-item">
                <Text data-testid="create-button-no-types-text">
                  No moment types available
                </Text>
              </Item>
            ) : (
              momentTypes
                .filter(type => !isLegacyOfferOrPromoMomentType(type))
                .map(type => {
                  // Disable Event and Key Date options for non-admins
                  const isEventOrKeyDate =
                    type.name === 'Event' || type.name === 'Key Date';

                  // If user profile is still loading, don't show admin restrictions yet
                  const shouldShowAdminOnly =
                    isEventOrKeyDate && !isUserProfileLoading && !isAdmin;

                  return (
                    <Item
                      key={type.id}
                      textValue={type.name}
                      data-testid={`create-button-moment-type-${type.id}`}
                    >
                      <Text
                        data-testid={`create-button-moment-type-text-${type.id}`}
                      >
                        {type.name}
                        {shouldShowAdminOnly && ' (Admin Only)'}
                        {isUserProfileLoading &&
                          isEventOrKeyDate &&
                          ' (Loading...)'}
                      </Text>
                    </Item>
                  );
                })
            )}
          </Menu>
        </MenuTrigger>
      )}
      <DialogTrigger
        isOpen={isDialogOpen}
        onOpenChange={open => {
          setIsDialogOpen(open);
          if (!open) setIsMenuOpen(false);
        }}
        data-testid="create-button-dialog-trigger"
      >
        <></>
        {close => (
          <Dialog
            size="L"
            UNSAFE_className={styles.dialog}
            data-testid="create-button-dialog"
          >
            <Content
              UNSAFE_className={styles.content}
              data-testid="create-button-dialog-content"
            >
              <CreateMoment
                onClose={close}
                momentTypeId={selectedMomentType}
                onSuccess={onSuccess}
                data-testid="create-button-create-moment"
              />
            </Content>
          </Dialog>
        )}
      </DialogTrigger>
    </Flex>
  );
};

// Use the main app store instead of separate Provider
const CreateButton = ({ onSuccess }: CreateButtonProps) => {
  return (
    <CreateButtonContent
      onSuccess={onSuccess}
      data-testid="create-button-content"
    />
  );
};

export default CreateButton;
export type { CreateButtonProps };
